// Sublote e Backlog de Relavagem (ticket #27, ADR-0007), estendido com o
// Backlog Inicial (issue #68, ADR-0019). O conjunto de retenções de um Lote
// **é** o Sublote dele — código do pai + "-R". O Backlog Inicial é o saldo que
// já existia num Cliente antes do sistema entrar em operação, sem Lote de
// origem. O saldo é por (Tipo, motivo) e a baixa é FIFO — Backlog Inicial
// sempre primeiro, depois os Sublotes do mais antigo para o mais novo.
// Funções puras: nada aqui grava, só lê e calcula.

import { newId, type Id } from "./ids.ts";
import { formatData, isDataISO, type DataISO } from "./datas.ts";
import { fechamentoDoLote, momentoDevolucao } from "./fechamento-do-lote.ts";
import type {
  BaixaBacklog,
  BaixaBacklogInicial,
  BacklogInicialLinha,
  Lote,
  Perda,
  Retencao,
  Snapshot, DestinoBaixa } from "./snapshot.ts";

export type { BaixaBacklog, BaixaBacklogInicial, BacklogInicialLinha, Perda, Retencao };

/** Motivo interno das linhas de Backlog Inicial — não nasceram de uma Devolução
 * específica, então não há um Motivo de Retenção de verdade para discriminar
 * (ADR-0019). Não aparece na lista editável de motivos. */
export const MOTIVO_BACKLOG_INICIAL = "Saldo inicial";

/** O Sublote se identifica pelo Lote pai: no máximo um por Lote. */
export function codigoSublote(lote: Pick<Lote, "codigo">): string {
  return `${lote.codigo}-R`;
}

/**
 * Uma linha de saldo do Backlog: de qual Cliente, de que Tipo, por que motivo.
 * `origemLoteId` é o Lote dono do Sublote, ou `null` para o Backlog Inicial
 * (que não tem Lote de origem nenhum).
 */
export interface SaldoLinha {
  clienteId: Id;
  origemLoteId: Id | null;
  tipoId: Id;
  motivo: string;
  quantidade: number;
}

type Escopo = Pick<
  Snapshot,
  | "retencoes"
  | "baixasBacklog"
  | "lotes"
  | "backlogInicial"
  | "baixasBacklogInicial"
  | "perdas"
>;

/**
 * Chave de um saldo de Sublote real: (Lote de origem, Tipo, motivo). Serializa
 * a tupla em vez de juntar com um separador — o motivo é texto livre ("Não
 * terminou de passar") e qualquer separador escolhido poderia aparecer nele e
 * colidir.
 */
export function chaveSaldo(o: Id, t: Id, m: string): string {
  return JSON.stringify([o, t, m]);
}
const chave = chaveSaldo;

/** Chave de uma linha de Backlog Inicial: (Cliente, Tipo) — sem Lote, sem motivo. */
function chaveInicial(clienteId: Id, tipoId: Id): string {
  return JSON.stringify([clienteId, tipoId]);
}

/** Quanto já foi baixado do Backlog Inicial de um Cliente/Tipo, somando todas
 * as Devoluções que já pegaram carona nele. */
export function baixadoBacklogInicial(
  snapshot: Pick<Snapshot, "baixasBacklogInicial">,
  clienteId: Id,
  tipoId: Id,
): number {
  return snapshot.baixasBacklogInicial
    .filter((b) => b.clienteId === clienteId && b.tipoId === tipoId)
    .reduce((a, b) => a + b.quantidade, 0);
}

/** Quanto já saiu como Perda do Backlog Inicial de um Cliente/Tipo. */
export function perdidoBacklogInicial(
  snapshot: Pick<Snapshot, "perdas">,
  clienteId: Id,
  tipoId: Id,
): number {
  return snapshot.perdas
    .filter(
      (p) => p.origemLoteId === null && p.clienteId === clienteId && p.tipoId === tipoId,
    )
    .reduce((a, p) => a + p.quantidade, 0);
}

/**
 * Saldo do Backlog Inicial de um Cliente/Tipo agora: o que foi cadastrado
 * menos o que já foi baixado dele. Pode dar **negativo** — é o único saldo do
 * Backlog que pode: cadastrado negativo pela tela (ADR-0023), ou deixado
 * assim por Fechamentos anteriores ao ADR-0027, quando o sistema ainda jogava
 * aqui a sobra da carona. Fica registrado até alguém corrigir o cadastro.
 */
export function saldoInicialDisponivel(
  snapshot: Escopo,
  clienteId: Id,
  tipoId: Id,
): number {
  const cadastrado =
    snapshot.backlogInicial.find((b) => b.clienteId === clienteId && b.tipoId === tipoId)
      ?.quantidade ?? 0;
  return (
    cadastrado -
    baixadoBacklogInicial(snapshot, clienteId, tipoId) -
    perdidoBacklogInicial(snapshot, clienteId, tipoId)
  );
}

/**
 * Saldo aberto do Backlog (retido menos o que já voltou, mais o Backlog
 * Inicial), em ordem **FIFO**: o Backlog Inicial de cada Cliente/Tipo vem
 * sempre primeiro (é anterior a qualquer Sublote real, por definição —
 * ADR-0019); dentro dos Sublotes reais, o mais antigo primeiro pela data/hora
 * de coleta do Lote de origem, e dentro do mesmo Sublote e Tipo, na ordem em
 * que os motivos foram retidos.
 *
 * `clienteId` filtra por Cliente (o "chiqueirinho" é por hotel). Um Lote
 * cancelado não entra — as suas retenções somem no cancelamento. Uma linha de
 * Backlog Inicial com saldo exatamente zero não aparece (nada a mostrar); uma
 * negativa aparece — é o sinal de cadastro incompleto que a tela precisa
 * exibir.
 */
export function saldoBacklog(snapshot: Escopo, clienteId?: Id | null): SaldoLinha[] {
  const lotePorId = new Map(snapshot.lotes.map((l) => [l.id, l]));

  // --- Sublotes reais (Retenção menos BaixaBacklog) ---
  const baixadoReal = new Map<string, number>();
  for (const b of snapshot.baixasBacklog) {
    const k = chave(b.origemLoteId, b.tipoId, b.motivo);
    baixadoReal.set(k, (baixadoReal.get(k) ?? 0) + b.quantidade);
  }
  // A Perda sai da mesma linha de saldo que uma baixa de carona sairia
  // (ADR-0022): a diferença é que a Peça não voltou ao Cliente.
  for (const p of snapshot.perdas) {
    if (p.origemLoteId === null) continue;
    const k = chave(p.origemLoteId, p.tipoId, p.motivoOrigem);
    baixadoReal.set(k, (baixadoReal.get(k) ?? 0) + p.quantidade);
  }

  // ordem de inserção das retenções = ordem de retenção dentro do Sublote
  const reaisBrutas: (SaldoLinha & { chaveOrdem: string; ordem: number })[] = [];
  const vistos = new Map<string, number>();
  snapshot.retencoes.forEach((r, i) => {
    const origem = lotePorId.get(r.loteId);
    if (!origem || origem.canceladoEm) return;
    if (clienteId && origem.clienteId !== clienteId) return;
    const k = chave(r.loteId, r.tipoId, r.motivo);
    const jaEm = vistos.get(k);
    if (jaEm != null) {
      reaisBrutas[jaEm]!.quantidade += r.quantidade;
      return;
    }
    vistos.set(k, reaisBrutas.length);
    reaisBrutas.push({
      clienteId: origem.clienteId,
      origemLoteId: r.loteId,
      tipoId: r.tipoId,
      motivo: r.motivo,
      quantidade: r.quantidade,
      chaveOrdem: `1${origem.coletaData}${origem.coletaHora}`,
      ordem: i,
    });
  });
  const reais = reaisBrutas
    .map((l) => ({
      ...l,
      quantidade: l.quantidade - (baixadoReal.get(chave(l.origemLoteId!, l.tipoId, l.motivo)) ?? 0),
    }))
    .filter((l) => l.quantidade > 0);

  // --- Backlog Inicial ---
  // Os pares (Cliente, Tipo) a considerar são a UNIÃO do que foi cadastrado
  // com o que já foi baixado — um Cliente/Tipo nunca cadastrado mas que já
  // teve uma Devolução puxando dele (sem saldo pra cobrir) também precisa
  // aparecer, negativo, mesmo sem nenhum registro em `backlogInicial`.
  const paresIniciais = new Map<string, { clienteId: Id; tipoId: Id }>();
  for (const b of snapshot.backlogInicial) {
    if (clienteId && b.clienteId !== clienteId) continue;
    paresIniciais.set(chaveInicial(b.clienteId, b.tipoId), { clienteId: b.clienteId, tipoId: b.tipoId });
  }
  for (const b of snapshot.baixasBacklogInicial) {
    if (clienteId && b.clienteId !== clienteId) continue;
    paresIniciais.set(chaveInicial(b.clienteId, b.tipoId), { clienteId: b.clienteId, tipoId: b.tipoId });
  }
  const iniciais: (SaldoLinha & { chaveOrdem: string; ordem: number })[] = [];
  [...paresIniciais.values()].forEach(({ clienteId: c, tipoId: t }, i) => {
    const disp = saldoInicialDisponivel(snapshot, c, t);
    if (disp === 0) return;
    iniciais.push({
      clienteId: c,
      origemLoteId: null,
      tipoId: t,
      motivo: MOTIVO_BACKLOG_INICIAL,
      quantidade: disp,
      // "0" ordena antes de "1..." (coletaData começa em "20..."): o Backlog
      // Inicial sempre fica na frente de qualquer Sublote real do mesmo par.
      chaveOrdem: "0",
      ordem: i,
    });
  });

  return [...iniciais, ...reais]
    .sort((a, b) =>
      a.chaveOrdem === b.chaveOrdem ? a.ordem - b.ordem : a.chaveOrdem.localeCompare(b.chaveOrdem),
    )
    .map(({ clienteId: c, origemLoteId, tipoId, motivo, quantidade }) => ({
      clienteId: c,
      origemLoteId,
      tipoId,
      motivo,
      quantidade,
    }));
}

/**
 * Uma linha do **histórico** do Backlog: uma unidade de (Sublote ou Backlog
 * Inicial, Tipo, motivo) em uma das 3 situações possíveis — ainda parada
 * (`"aberto"`, o mesmo que `saldoBacklog` já mostra), já voltou de carona
 * numa Devolução seguinte (`"devolvido"` — hoje essa informação não aparece
 * em lugar nenhum: a baixa consome o saldo em silêncio), ou saiu de vez por
 * uma Perda (`"perda"`, ADR-0022). `"entrada"` é o movimento contrário: o
 * cadastro do Backlog Inicial ou as retidas de uma OS entrando no Backlog —
 * com elas o Histórico tem saldo corrido (ADR-0030).
 */
export interface LinhaHistorico {
  clienteId: Id;
  origemLoteId: Id | null;
  tipoId: Id;
  motivo: string;
  quantidade: number;
  /** Coleta do Lote de origem; `null` para uma linha de Backlog Inicial. */
  entrouEm: DataISO | null;
  situacao: "aberto" | "entrada" | "devolvido" | "retida" | "perda";
  /** OS em que a Peça saiu (devolvida ou retida de novo); `null` nas demais. */
  destinoLoteId: Id | null;
  /** `null` enquanto `situacao` for `"aberto"` ou `"entrada"`. */
  saiuEm: DataISO | null;
  /**
   * Quando o saldo mudou: a devolução da OS numa `"entrada"` (`null` no
   * cadastro do Backlog Inicial, que não tem data), `saiuEm` numa saída,
   * `null` no `"aberto"`.
   */
  movimentoEm: DataISO | null;
  /** Motivo de divergência da Perda — só quando `situacao === "perda"`. */
  motivoSaida: string | null;
  /** Id da Perda — só quando `situacao === "perda"` (para o botão Excluir). */
  perdaId: Id | null;
  /**
   * Quantas Peças deste Tipo o Cliente tinha no Backlog inteiro (todos os
   * Sublotes + Backlog Inicial) logo antes deste movimento. `null` nas
   * linhas "aberto" — não são um movimento.
   */
  saldoAntes: number | null;
  /** Depois do movimento: `saldoAntes + quantidade` numa entrada,
   * `saldoAntes − quantidade` numa saída. */
  saldoDepois: number | null;
}

type LinhaSemSaldo = Omit<LinhaHistorico, "saldoAntes" | "saldoDepois" | "movimentoEm">;

/**
 * O histórico completo do Backlog, com saldo corrido (ADR-0030): o que está aberto
 * (mesmas linhas de `saldoBacklog`), o que entrou (cadastro do Backlog
 * Inicial e as retidas de cada OS) e o que já saiu — de carona numa Devolução
 * seguinte, ou por uma Perda — cada movimento com sua própria data. Vêm
 * primeiro as linhas abertas, depois os movimentos do mais recente para o mais
 * antigo. Uma retenção parcialmente
 * consumida aparece em mais de uma linha (o resto ainda aberto, mais uma
 * linha por saída registrada) — cada `BaixaBacklog`/`BaixaBacklogInicial`/
 * `Perda` é um evento próprio, com sua própria data, então não dá para
 * comprimir tudo numa linha só por saldo.
 *
 * Um Lote cancelado não aparece em nenhuma situação (mesma regra de
 * `saldoBacklog`: suas retenções somem no cancelamento, e `cancelarLote`
 * nunca deixa uma baixa referenciar um Lote de origem cancelado).
 */
export function historicoBacklog(snapshot: Escopo): LinhaHistorico[] {
  const lotePorId = new Map(snapshot.lotes.map((l) => [l.id, l]));
  const linhas: LinhaSemSaldo[] = [];

  for (const l of saldoBacklog(snapshot)) {
    const origem = l.origemLoteId ? lotePorId.get(l.origemLoteId) : null;
    linhas.push({
      ...l,
      entrouEm: origem?.coletaData ?? null,
      situacao: "aberto",
      saiuEm: null,
      motivoSaida: null,
      perdaId: null,
      destinoLoteId: null,
    });
  }

  for (const b of snapshot.baixasBacklog) {
    const origem = lotePorId.get(b.origemLoteId);
    if (!origem) continue; // defensivo: nunca deveria faltar
    const destino = lotePorId.get(b.loteId);
    linhas.push({
      clienteId: origem.clienteId,
      origemLoteId: b.origemLoteId,
      tipoId: b.tipoId,
      motivo: b.motivo,
      quantidade: b.quantidade,
      entrouEm: origem.coletaData,
      situacao: b.destino === "retida" ? "retida" : "devolvido",
      saiuEm: destino ? (fechamentoDoLote(destino)?.data ?? destino.coletaData) : null,
      motivoSaida: null,
      perdaId: null,
      destinoLoteId: b.loteId,
    });
  }

  for (const b of snapshot.baixasBacklogInicial) {
    const destino = lotePorId.get(b.loteId);
    linhas.push({
      clienteId: b.clienteId,
      origemLoteId: null,
      tipoId: b.tipoId,
      motivo: MOTIVO_BACKLOG_INICIAL,
      quantidade: b.quantidade,
      entrouEm: null,
      situacao: b.destino === "retida" ? "retida" : "devolvido",
      saiuEm: destino ? (fechamentoDoLote(destino)?.data ?? destino.coletaData) : null,
      motivoSaida: null,
      perdaId: null,
      destinoLoteId: b.loteId,
    });
  }

  for (const p of snapshot.perdas) {
    const origem = p.origemLoteId ? lotePorId.get(p.origemLoteId) : null;
    linhas.push({
      clienteId: p.clienteId,
      origemLoteId: p.origemLoteId,
      tipoId: p.tipoId,
      motivo: p.motivoOrigem,
      quantidade: p.quantidade,
      entrouEm: origem?.coletaData ?? null,
      situacao: "perda",
      saiuEm: p.data,
      motivoSaida: p.motivo,
      perdaId: p.id,
      destinoLoteId: null,
    });
  }

  // --- entradas: o cadastro do Backlog Inicial e as retidas de cada OS. Com
  // elas toda variação do saldo tem uma linha, e o Eram/Ficaram de uma linha
  // emenda no da seguinte (ADR-0030).
  for (const b of snapshot.backlogInicial) {
    if (b.quantidade === 0) continue;
    linhas.push({
      clienteId: b.clienteId,
      origemLoteId: null,
      tipoId: b.tipoId,
      motivo: MOTIVO_BACKLOG_INICIAL,
      quantidade: b.quantidade,
      entrouEm: null,
      situacao: "entrada",
      saiuEm: null,
      motivoSaida: null,
      perdaId: null,
      destinoLoteId: null,
    });
  }
  for (const r of snapshot.retencoes) {
    const origem = lotePorId.get(r.loteId);
    if (!origem || origem.canceladoEm) continue;
    linhas.push({
      clienteId: origem.clienteId,
      origemLoteId: r.loteId,
      tipoId: r.tipoId,
      motivo: r.motivo,
      quantidade: r.quantidade,
      entrouEm: origem.coletaData,
      situacao: "entrada",
      saiuEm: null,
      motivoSaida: null,
      perdaId: null,
      destinoLoteId: null,
    });
  }

  return comSaldoClienteTipo(snapshot, linhas);
}

// A ordem dos Fechamentos (ADR-0028) mora em fechamento-do-lote.ts (issue #90).

/** A Perda só tem data: "~" ordena depois de qualquer "|hora" do mesmo dia. */
function momentoPerda(data: DataISO): string {
  return `${data}~`;
}

/** Peças que entraram no Backlog depois de um momento, por OS de origem. */
export interface EntradaPosterior {
  os: number;
  /** Data e hora da devolução da OS de origem. */
  data: DataISO;
  hora: string | null;
  quantidade: number;
}

/**
 * O que entrou no Backlog de um Cliente num Tipo **depois** de `ate`
 * (`momentoDevolucao`): retenções de OS devolvidas mais tarde, em ordem de
 * devolução. Não conta para uma devolução em `ate` (ADR-0028) — é o que
 * explica um "a mais do que existia no Backlog" quando a tela de Backlog
 * mostra peças (OS 437/436).
 */
export function entradasDepoisDe(snapshot: Escopo, clienteId: Id, tipoId: Id, ate: string): EntradaPosterior[] {
  const porLote = new Map<Id, EntradaPosterior & { momento: string }>();
  for (const r of snapshot.retencoes) {
    if (r.tipoId !== tipoId) continue;
    const l = snapshot.lotes.find((x) => x.id === r.loteId);
    if (!l || l.clienteId !== clienteId || l.canceladoEm) continue;
    const momento = momentoDevolucao(l);
    if (momento <= ate) continue;
    const f = fechamentoDoLote(l);
    const atual = porLote.get(l.id) ?? {
      os: l.os,
      data: f?.data ?? l.coletaData,
      hora: f?.hora ?? null,
      quantidade: 0,
      momento,
    };
    atual.quantidade += r.quantidade;
    porLote.set(l.id, atual);
  }
  return [...porLote.values()]
    .sort((a, b) => (a.momento < b.momento ? -1 : 1))
    .map(({ momento: _m, ...e }) => e);
}

/**
 * Quantas Peças de um Tipo o Cliente tinha no Backlog inteiro num momento
 * (`momentoDevolucao`): Backlog Inicial cadastrado + retenções das OS
 * devolvidas antes − o que saiu antes (carona e Perda). Com `incluirIgual`,
 * conta também o que a própria OS daquele momento fez — é o saldo logo
 * depois dela. É a mesma conta do "Eram/Ficaram" do Histórico.
 */
export function saldoNoMomento(
  snapshot: Escopo,
  clienteId: Id,
  tipoId: Id,
  ate: string,
  incluirIgual = false,
): number {
  const lotePorId = new Map(snapshot.lotes.map((l) => [l.id, l]));
  const conta = (k: string): boolean => k < ate || (incluirIgual && k === ate);
  const momentoDe = (id: Id): string | null => {
    const l = lotePorId.get(id);
    return !l || l.canceladoEm ? null : momentoDevolucao(l);
  };
  let saldo = 0;
  for (const b of snapshot.backlogInicial) {
    if (b.clienteId === clienteId && b.tipoId === tipoId) saldo += b.quantidade;
  }
  for (const r of snapshot.retencoes) {
    if (r.tipoId !== tipoId || lotePorId.get(r.loteId)?.clienteId !== clienteId) continue;
    const k = momentoDe(r.loteId);
    if (k != null && conta(k)) saldo += r.quantidade;
  }
  for (const b of snapshot.baixasBacklog) {
    if (b.tipoId !== tipoId || lotePorId.get(b.origemLoteId)?.clienteId !== clienteId) continue;
    const k = momentoDe(b.loteId);
    if (k != null && conta(k)) saldo -= b.quantidade;
  }
  for (const b of snapshot.baixasBacklogInicial) {
    if (b.tipoId !== tipoId || b.clienteId !== clienteId) continue;
    const k = momentoDe(b.loteId);
    if (k != null && conta(k)) saldo -= b.quantidade;
  }
  for (const p of snapshot.perdas) {
    if (p.tipoId === tipoId && p.clienteId === clienteId && conta(momentoPerda(p.data))) saldo -= p.quantidade;
  }
  return saldo;
}

/**
 * Preenche `saldoAntes`/`saldoDepois` de cada movimento do histórico, no
 * nível Cliente × Tipo: "quanto tinha, quanto entrou ou saiu, quanto ficou".
 * Reconstrói o saldo em ordem cronológica de devolução (ADR-0028):
 * - começa no Backlog Inicial cadastrado (é o mais antigo de todos);
 * - cada Fechamento de OS primeiro tira a carona (devolvida ou retida de
 *   novo) e depois soma as retidas dele — a Peça retida de novo sai da
 *   origem e volta como parte do Sublote desta OS (ADR-0025);
 * - a Perda (só tem data) sai depois dos Fechamentos do mesmo dia.
 * Lote cancelado não conta, igual a `saldoBacklog` — o saldo final de cada
 * par bate com a soma das linhas "aberto". Devolve as linhas abertas
 * primeiro e depois os movimentos, do mais recente para o mais antigo.
 */
function comSaldoClienteTipo(snapshot: Escopo, linhas: readonly LinhaSemSaldo[]): LinhaHistorico[] {
  const lotePorId = new Map(snapshot.lotes.map((l) => [l.id, l]));
  const chaveLote = (id: Id): string => {
    const l = lotePorId.get(id);
    return l ? momentoDevolucao(l) : "";
  };
  const par = (c: Id, t: Id): string => JSON.stringify([c, t]);

  // sub-ordem dentro do mesmo instante, na ordem do FIFO (`alocarFifo`):
  // 0 = carona do Backlog Inicial, 1 = carona de Sublote real, 2 = entrada
  // (retidas da OS). O cadastro do Backlog Inicial tem chave "" (antes de
  // tudo); a Perda fica no fim do dia.
  const eventos: { par: string; chave: string; sub: number; seq: number; delta: number; linha: number }[] = [];
  linhas.forEach((l, i) => {
    if (l.situacao === "aberto") return;
    const p = par(l.clienteId, l.tipoId);
    if (l.situacao === "entrada") {
      const chave = l.origemLoteId ? chaveLote(l.origemLoteId) : "";
      eventos.push({ par: p, chave, sub: l.origemLoteId ? 2 : 0, seq: i, delta: l.quantidade, linha: i });
      return;
    }
    const chave =
      l.situacao === "perda" ? momentoPerda(l.saiuEm ?? "") : l.destinoLoteId ? chaveLote(l.destinoLoteId) : "";
    eventos.push({ par: p, chave, sub: l.origemLoteId === null ? 0 : 1, seq: i, delta: -l.quantidade, linha: i });
  });
  const ordem = (a: (typeof eventos)[number], b: (typeof eventos)[number]): number =>
    a.chave !== b.chave ? (a.chave < b.chave ? -1 : 1) : a.sub !== b.sub ? a.sub - b.sub : a.seq - b.seq;
  eventos.sort(ordem);

  const saldo = new Map<string, number>();
  const antesPorLinha = new Map<number, number>();
  for (const e of eventos) {
    const atual = saldo.get(e.par) ?? 0;
    antesPorLinha.set(e.linha, atual);
    saldo.set(e.par, atual + e.delta);
  }

  const movimentoEm = (l: LinhaSemSaldo): DataISO | null => {
    if (l.situacao === "aberto") return null;
    if (l.situacao !== "entrada") return l.saiuEm;
    const o = l.origemLoteId ? lotePorId.get(l.origemLoteId) : null;
    return o ? (fechamentoDoLote(o)?.data ?? null) : null;
  };
  const completa = (i: number): LinhaHistorico => {
    const l = linhas[i]!;
    const antes = antesPorLinha.get(i);
    const sinal = l.situacao === "entrada" ? 1 : -1;
    return antes == null
      ? { ...l, movimentoEm: movimentoEm(l), saldoAntes: null, saldoDepois: null }
      : { ...l, movimentoEm: movimentoEm(l), saldoAntes: antes, saldoDepois: antes + sinal * l.quantidade };
  };
  const abertas = linhas.flatMap((l, i) => (l.situacao === "aberto" ? [completa(i)] : []));
  const movimentos = [...eventos].reverse().map((e) => completa(e.linha));
  return [...abertas, ...movimentos];
}

/** O saldo de um Sublote (as retidas de um Lote que ainda não voltaram). */
export function saldoSublote(snapshot: Escopo, origemLoteId: Id): SaldoLinha[] {
  return saldoBacklog(snapshot).filter((l) => l.origemLoteId === origemLoteId);
}

export function situacaoSublote(
  snapshot: Escopo,
  origemLoteId: Id,
): "aberto" | "quitado" {
  return saldoSublote(snapshot, origemLoteId).length > 0 ? "aberto" : "quitado";
}

/**
 * Quantas Peças de um Tipo estão no Backlog do Cliente — Sublotes reais mais o
 * Backlog Inicial, podendo dar negativo se o Backlog Inicial estiver assim.
 * `excluirLoteId` tira da conta o Sublote do próprio Lote — não se devolve
 * numa Devolução o que ficou retido nela mesma.
 */
export function saldoDisponivel(
  snapshot: Escopo,
  clienteId: Id,
  tipoId: Id,
  excluirLoteId?: Id,
): number {
  return saldoBacklog(snapshot, clienteId)
    .filter((l) => l.tipoId === tipoId && l.origemLoteId !== excluirLoteId)
    .reduce((a, l) => a + l.quantidade, 0);
}

/** Peças do Backlog de uma data que saíram por uma OS devolvida depois dela. */
export interface SaidaPosterior {
  /** OS que levou as peças (carona); `null` = Perda. */
  loteId: Id | null;
  os: number | null;
  data: DataISO;
  hora: string | null;
  quantidade: number;
}

/**
 * O Backlog do Cliente num Tipo **num momento** (`momentoDevolucao`) — a
 * definição única que o Fechamento de OS lê (#86):
 * - `noMomento`: quantas havia na data (o que a conta e a Retida usam);
 * - `entraramDepois` / `sairamDepois`: o que explica a diferença entre a data
 *   e agora (OS 437/436; opção A do dono no #86).
 * A carona (`alocarFifo`) lê as mesmas origens (`origemExistiaNaData`), mas
 * **pelo saldo de agora**: peça que uma OS devolvida depois (e que não será
 * refeita, ex.: faturada) já levou não sai duas vezes (Sublote nunca negativo).
 */
export function backlogNoMomento(
  snapshot: Escopo,
  clienteId: Id,
  tipoId: Id,
  ate: string,
): { noMomento: number; entraramDepois: EntradaPosterior[]; sairamDepois: SaidaPosterior[] } {
  const lotePorId = new Map(snapshot.lotes.map((l) => [l.id, l]));
  const momentoDe = (id: Id): string | null => {
    const l = lotePorId.get(id);
    return !l || l.canceladoEm ? null : momentoDevolucao(l);
  };
  const existia = origemExistiaNaData(snapshot, ate);

  const porLote = new Map<string, SaidaPosterior>();
  const somarSaida = (loteId: Id | null, quantidade: number, data: DataISO, hora: string | null, os: number | null): void => {
    const chave = loteId ?? `perda|${data}`;
    const atual = porLote.get(chave) ?? { loteId, os, data, hora, quantidade: 0 };
    atual.quantidade += quantidade;
    porLote.set(chave, atual);
  };
  const saiuDepois = (loteId: Id, quantidade: number): void => {
    const k = momentoDe(loteId);
    const l = lotePorId.get(loteId);
    if (k == null || !l || k <= ate) return;
    const f = fechamentoDoLote(l);
    const data = f?.data ?? l.coletaData;
    const hora = f?.hora ?? null;
    somarSaida(loteId, quantidade, data, hora, l.os);
  };
  for (const b of snapshot.baixasBacklogInicial) {
    if (b.clienteId === clienteId && b.tipoId === tipoId) saiuDepois(b.loteId, b.quantidade);
  }
  for (const b of snapshot.baixasBacklog) {
    if (b.tipoId !== tipoId || lotePorId.get(b.origemLoteId)?.clienteId !== clienteId) continue;
    if (existia(b.origemLoteId)) saiuDepois(b.loteId, b.quantidade);
  }
  for (const p of snapshot.perdas) {
    if (p.tipoId !== tipoId || p.clienteId !== clienteId || !existia(p.origemLoteId)) continue;
    if (momentoPerda(p.data) > ate) somarSaida(null, p.quantidade, p.data, null, null);
  }

  return {
    noMomento: saldoNoMomento(snapshot, clienteId, tipoId, ate),
    entraramDepois: entradasDepoisDe(snapshot, clienteId, tipoId, ate),
    sairamDepois: [...porLote.values()].sort((a, b) => `${a.data}${a.hora ?? ""}`.localeCompare(`${b.data}${b.hora ?? ""}`)),
  };
}

/**
 * A origem de uma peça já existia na data `ate` (`momentoDevolucao`)? O
 * Backlog Inicial sempre (é o mais antigo de todos); um Sublote, se a OS dele
 * foi devolvida antes e não está cancelada. Uma regra só para a conta e a
 * carona (ADR-0028, #86).
 */
export function origemExistiaNaData(snapshot: Escopo, ate: string): (origemLoteId: Id | null) => boolean {
  const lotePorId = new Map(snapshot.lotes.map((l) => [l.id, l]));
  return (origemLoteId) => {
    if (origemLoteId === null) return true;
    const l = lotePorId.get(origemLoteId);
    return l != null && !l.canceladoEm && momentoDevolucao(l) < ate;
  };
}

/**
 * De onde a carona pode sair, em ordem FIFO, pelo saldo de agora: primeiro o
 * Backlog Inicial (só o positivo), depois os Sublotes abertos do mais antigo
 * para o mais novo que `existia` aceita.
 */
function disponiveisFifo(
  snapshot: Escopo,
  clienteId: Id,
  tipoId: Id,
  excluirLoteId: Id | undefined,
  existia: (origemLoteId: Id) => boolean,
): SaldoLinha[] {
  const linhas: SaldoLinha[] = [];
  const saldoInicial = saldoInicialDisponivel(snapshot, clienteId, tipoId);
  if (saldoInicial > 0) {
    linhas.push({ clienteId, origemLoteId: null, tipoId, motivo: MOTIVO_BACKLOG_INICIAL, quantidade: saldoInicial });
  }
  for (const l of saldoBacklog(snapshot, clienteId)) {
    if (l.tipoId !== tipoId || l.origemLoteId === null || l.origemLoteId === excluirLoteId) continue;
    if (existia(l.origemLoteId)) linhas.push(l);
  }
  return linhas;
}

/**
 * Distribui `quantidade` Peças de um Tipo pelo Backlog do Cliente, sempre
 * nesta ordem: primeiro o Backlog Inicial (até o que ele tiver de saldo
 * positivo agora), depois os Sublotes reais abertos do mais antigo para o
 * mais novo (nunca ultrapassando o que cada um tem — ADR-0007). Nunca aloca
 * mais do que existe: o Backlog Inicial só é preenchido pela tela, nunca pelo
 * sistema (ADR-0027). Se faltar, o plano sai menor que o pedido e quem chama
 * decide — o Fechamento de OS recusa, a Ficha avisa.
 *
 * `ate` (um `momentoDevolucao`) deixa de fora os Sublotes de OS devolvidas
 * depois dele: peça retida numa devolução posterior ainda não existia
 * (ADR-0028). O Backlog Inicial é o mais antigo de todos — sempre entra.
 */
export function alocarFifo(
  snapshot: Escopo,
  clienteId: Id,
  tipoId: Id,
  quantidade: number,
  excluirLoteId?: Id,
  ate?: string,
): SaldoLinha[] {
  if (quantidade <= 0) return [];
  const existia = ate == null ? () => true : origemExistiaNaData(snapshot, ate);
  // Sublote nunca ultrapassado (ADR-0007); o que faltar não sai de lugar
  // nenhum — o Backlog Inicial não fica negativo sozinho (ADR-0027).
  let faltam = quantidade;
  const saida: SaldoLinha[] = [];
  for (const l of disponiveisFifo(snapshot, clienteId, tipoId, excluirLoteId, existia)) {
    if (faltam === 0) break;
    const usa = Math.min(faltam, l.quantidade);
    saida.push({ ...l, quantidade: usa });
    faltam -= usa;
  }
  return saida;
}

/**
 * Quanto uma Devolução (Lote carona) puxou do Backlog — Sublotes reais e
 * Backlog Inicial somados, sem distinguir a origem. É o número que a Fatura
 * cobra: para o cliente, uma Peça que voltou do Backlog Inicial vale tanto
 * quanto uma que voltou de um Sublote real (ADR-0019) — ambas eram sujeira que
 * ele já tinha coletado antes e não veio contada nesta comanda.
 */
export function baixasDoLote(
  snapshot: Pick<Snapshot, "baixasBacklog" | "baixasBacklogInicial">,
  loteId: Id,
  tipoId?: Id,
): number {
  return somaBaixas(snapshot, loteId, "devolvida", tipoId);
}

/**
 * Quanto uma OS tirou do Backlog e reteve DE NOVO (ADR-0025): Peças de OS
 * anteriores (ou do Backlog Inicial) que foram reprocessadas e ficaram
 * retidas nesta — saem da origem, contam no Sublote desta OS e não são
 * faturadas.
 */
export function retidasDeNovoDoLote(
  snapshot: Pick<Snapshot, "baixasBacklog" | "baixasBacklogInicial">,
  loteId: Id,
  tipoId?: Id,
): number {
  return somaBaixas(snapshot, loteId, "retida", tipoId);
}

function somaBaixas(
  snapshot: Pick<Snapshot, "baixasBacklog" | "baixasBacklogInicial">,
  loteId: Id,
  destino: DestinoBaixa,
  tipoId?: Id,
): number {
  const casa = (b: { loteId: Id; tipoId: Id; destino: DestinoBaixa }): boolean =>
    b.loteId === loteId && b.destino === destino && (tipoId == null || b.tipoId === tipoId);
  return [...snapshot.baixasBacklog, ...snapshot.baixasBacklogInicial]
    .filter(casa)
    .reduce((a, b) => a + b.quantidade, 0);
}

/**
 * O total que saiu fisicamente neste Fechamento de OS — o número que se
 * digita em "Devolvida": a parte própria do Lote (`Contagem.devolvida`) mais
 * o que ela puxou do Backlog de carona (`baixasDoLote`). É o que a Ficha
 * mostra ao reabrir e o que a lista de OS exibe; ler só a parte própria faz
 * o valor digitado "mudar" (36 digitado reaparecia como 27 — OS 874).
 * Sem `tipoId`, soma todos os Tipos do Lote.
 */
export function devolvidaEntregue(
  snapshot: Pick<Snapshot, "contagens" | "baixasBacklog" | "baixasBacklogInicial">,
  loteId: Id,
  tipoId?: Id,
): number {
  const propria = snapshot.contagens
    .filter((c) => c.loteId === loteId && (tipoId == null || c.tipoId === tipoId))
    .reduce((a, c) => a + c.devolvida, 0);
  return propria + baixasDoLote(snapshot, loteId, tipoId);
}

/** Os Tipos que uma Devolução (Lote carona) baixou do Backlog — Sublotes
 * reais e Backlog Inicial somados (ADR-0019). */
export function tiposBaixadosDoLote(
  snapshot: Pick<Snapshot, "baixasBacklog" | "baixasBacklogInicial">,
  loteId: Id,
): Id[] {
  return [
    ...new Set([
      ...snapshot.baixasBacklog.filter((b) => b.loteId === loteId).map((b) => b.tipoId),
      ...snapshot.baixasBacklogInicial.filter((b) => b.loteId === loteId).map((b) => b.tipoId),
    ]),
  ];
}

// ------------------------------------------------------------------ perda ----

export class ErroPerda extends Error {}

export type DadosPerda = Omit<Perda, "id">;

/**
 * Tira de vez do Backlog a Peça que não volta mais (ADR-0022). Mira uma linha
 * de saldo concreta — o operador está olhando para ela na tela —, então não
 * passa pela alocação FIFO e **nunca** deixa saldo negativo, nem no Backlog
 * Inicial.
 *
 * A data fica entre a entrada da Peça no Backlog (a devolução da OS de origem;
 * o Backlog Inicial não tem data) e `hoje` (ADR-0028) — senão o Histórico
 * mostraria a Peça saindo antes de existir.
 */
export function registrarPerda(snapshot: Snapshot, dados: DadosPerda, hoje: DataISO): Snapshot {
  if (!Number.isInteger(dados.quantidade) || dados.quantidade <= 0) {
    throw new ErroPerda("Informe uma quantidade maior que zero.");
  }
  const motivo = dados.motivo.trim();
  if (motivo === "") throw new ErroPerda("Informe o motivo da perda.");
  if (!isDataISO(dados.data)) throw new ErroPerda("Informe a data da perda.");
  if (dados.data > hoje) throw new ErroPerda("A data da perda não pode ser no futuro.");
  if (dados.origemLoteId !== null) {
    const origem = snapshot.lotes.find((l) => l.id === dados.origemLoteId);
    const entrou = origem ? (fechamentoDoLote(origem)?.data ?? null) : null;
    if (origem && entrou && dados.data < entrou) {
      throw new ErroPerda(
        `Estas peças entraram no Backlog em ${formatData(entrou)}, na devolução da OS ${origem.os} — a perda não pode ser antes disso.`,
      );
    }
  }

  const linha = saldoBacklog(snapshot, dados.clienteId).find(
    (l) =>
      l.clienteId === dados.clienteId &&
      l.tipoId === dados.tipoId &&
      l.origemLoteId === dados.origemLoteId &&
      l.motivo === dados.motivoOrigem,
  );
  if (!linha) throw new ErroPerda("Esta linha não está mais no Backlog.");
  if (dados.quantidade > linha.quantidade) {
    throw new ErroPerda(
      `O Backlog tem ${linha.quantidade} peça(s) nesta linha — não dá para registrar ${dados.quantidade}.`,
    );
  }

  const perda: Perda = { id: newId(), ...dados, motivo };
  return { ...snapshot, perdas: [...snapshot.perdas, perda] };
}

/** Desfaz uma Perda: o saldo volta para o Backlog. */
export function excluirPerda(snapshot: Snapshot, id: Id): Snapshot {
  if (!snapshot.perdas.some((p) => p.id === id)) {
    throw new ErroPerda("Perda não encontrada.");
  }
  return { ...snapshot, perdas: snapshot.perdas.filter((p) => p.id !== id) };
}

// ------------------------------------------------------------ invariantes ----

/**
 * As contas que nunca podem quebrar. Devolve a lista de problemas — vazia é o
 * estado bom. Roda nos testes depois de cada operação.
 *
 * O Backlog Inicial fica de fora do checo "nunca negativo": ele **pode**
 * ficar negativo — cadastrado assim pela tela (ADR-0023), ou por Fechamentos
 * anteriores ao ADR-0027. Como a baixa dele vive numa tabela
 * própria (`baixasBacklogInicial`), a checagem de Sublote real abaixo nem
 * enxerga essas linhas — não precisa de exceção nenhuma.
 */
export function verificarInvariantes(snapshot: Snapshot): string[] {
  const problemas: string[] = [];
  const loteIds = new Set(snapshot.lotes.map((l) => l.id));
  const tipoIds = new Set(snapshot.tiposPeca.map((t) => t.id));
  const clienteIds = new Set(snapshot.clientes.map((c) => c.id));

  for (const r of snapshot.retencoes) {
    if (!loteIds.has(r.loteId)) problemas.push(`retenção sem Lote de origem (${r.loteId})`);
    if (!tipoIds.has(r.tipoId)) problemas.push(`retenção sem Tipo (${r.tipoId})`);
    if (r.motivo.trim() === "") problemas.push("retenção sem motivo");
    if (!Number.isInteger(r.quantidade) || r.quantidade <= 0) {
      problemas.push(`retenção com quantidade inválida (${r.quantidade})`);
    }
  }

  const retido = new Map<string, number>();
  for (const r of snapshot.retencoes) {
    const k = chave(r.loteId, r.tipoId, r.motivo);
    retido.set(k, (retido.get(k) ?? 0) + r.quantidade);
  }
  const baixado = new Map<string, number>();
  for (const b of snapshot.baixasBacklog) {
    if (!loteIds.has(b.loteId)) problemas.push(`baixa sem Lote de carona (${b.loteId})`);
    if (!loteIds.has(b.origemLoteId)) {
      problemas.push(`baixa sem Sublote de origem (${b.origemLoteId})`);
    }
    if (b.loteId === b.origemLoteId) {
      problemas.push("baixa que sai do Sublote da própria Devolução");
    }
    const k = chave(b.origemLoteId, b.tipoId, b.motivo);
    baixado.set(k, (baixado.get(k) ?? 0) + b.quantidade);
  }
  for (const p of snapshot.perdas) {
    if (!clienteIds.has(p.clienteId)) problemas.push(`perda sem Cliente (${p.clienteId})`);
    if (!tipoIds.has(p.tipoId)) problemas.push(`perda sem Tipo (${p.tipoId})`);
    if (p.motivo.trim() === "") problemas.push("perda sem motivo");
    if (!Number.isInteger(p.quantidade) || p.quantidade <= 0) {
      problemas.push(`perda com quantidade inválida (${p.quantidade})`);
    }
    if (p.origemLoteId === null) continue;
    if (!loteIds.has(p.origemLoteId)) {
      problemas.push(`perda sem Sublote de origem (${p.origemLoteId})`);
    }
    // conta junto das baixas: a checagem de saldo negativo logo abaixo cobre
    const k = chave(p.origemLoteId, p.tipoId, p.motivoOrigem);
    baixado.set(k, (baixado.get(k) ?? 0) + p.quantidade);
  }
  for (const [k, qtd] of baixado) {
    const disp = retido.get(k) ?? 0;
    if (qtd > disp) {
      problemas.push(`saldo de Sublote negativo: baixado ${qtd} de ${disp} retido(s)`);
    }
  }

  const paresBacklogInicial = new Set<string>();
  for (const b of snapshot.backlogInicial) {
    if (!clienteIds.has(b.clienteId)) {
      problemas.push(`backlog inicial sem Cliente (${b.clienteId})`);
    }
    if (!tipoIds.has(b.tipoId)) problemas.push(`backlog inicial sem Tipo (${b.tipoId})`);
    if (!Number.isInteger(b.quantidade) || b.quantidade < 0) {
      problemas.push(`backlog inicial com quantidade inválida (${b.quantidade})`);
    }
    const k = chaveInicial(b.clienteId, b.tipoId);
    if (paresBacklogInicial.has(k)) {
      problemas.push(`backlog inicial duplicado para o mesmo Cliente/Tipo (${k})`);
    }
    paresBacklogInicial.add(k);
  }
  for (const b of snapshot.baixasBacklogInicial) {
    if (!loteIds.has(b.loteId)) problemas.push(`baixa de backlog inicial sem Lote de carona (${b.loteId})`);
    if (!clienteIds.has(b.clienteId)) {
      problemas.push(`baixa de backlog inicial sem Cliente (${b.clienteId})`);
    }
    if (!tipoIds.has(b.tipoId)) problemas.push(`baixa de backlog inicial sem Tipo (${b.tipoId})`);
    if (!Number.isInteger(b.quantidade) || b.quantidade <= 0) {
      problemas.push(`baixa de backlog inicial com quantidade inválida (${b.quantidade})`);
    }
  }
  // O Backlog Inicial pode estar negativo (cadastro pela tela, ou legado de
  // antes do ADR-0027), mas
  // nunca por Perda: ela só é aceita contra saldo positivo, então o total
  // perdido jamais pode passar do que foi cadastrado. Os pares a considerar são
  // a UNIÃO do que foi cadastrado com o que uma Perda mira — uma Perda contra
  // um (Cliente, Tipo) nunca cadastrado (`cadastrado` = 0) é tão inválida
  // quanto uma que excede o cadastro, e iterar só `backlogInicial` a deixaria
  // invisível aqui, do mesmo jeito que a checagem de Sublote real acima
  // enxerga o par sem retenção nenhuma (`retido` = 0) por não iterar retenções.
  const paresPerdaInicial = new Map<string, { clienteId: Id; tipoId: Id }>();
  for (const b of snapshot.backlogInicial) {
    paresPerdaInicial.set(chaveInicial(b.clienteId, b.tipoId), {
      clienteId: b.clienteId,
      tipoId: b.tipoId,
    });
  }
  for (const p of snapshot.perdas) {
    if (p.origemLoteId !== null) continue;
    paresPerdaInicial.set(chaveInicial(p.clienteId, p.tipoId), {
      clienteId: p.clienteId,
      tipoId: p.tipoId,
    });
  }
  for (const { clienteId: c, tipoId: t } of paresPerdaInicial.values()) {
    const cadastrado =
      snapshot.backlogInicial.find((b) => b.clienteId === c && b.tipoId === t)?.quantidade ?? 0;
    const perdido = perdidoBacklogInicial(snapshot, c, t);
    if (perdido > cadastrado) {
      problemas.push(
        `perda de Backlog Inicial acima do cadastrado: ${perdido} de ${cadastrado}`,
      );
    }
  }

  for (const c of snapshot.contagens) {
    if (c.recebida < 0 || c.devolvida < 0) {
      problemas.push(`contagem negativa no Lote ${c.loteId}`);
    }
  }
  return problemas;
}
